import React, { useState, useEffect, useCallback, useRef } from 'react'
import { XMarkIcon, MagnifyingGlassIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { Button } from '../../../components/ui/button'
import { getLocalApiUrl, authFetch } from '../../../config/api'
import { ECDD_TRIGGER_CONFIG } from '../../../data/ecddData'

const TRIGGER_OPTIONS = Object.entries(ECDD_TRIGGER_CONFIG).map(([value, cfg]) => ({
  value,
  label: cfg.label,
}))

const MIN_SEARCH_CHARS = 2
const SEARCH_DEBOUNCE_MS = 280

function patronLabel(p) {
  const name = [p.FN, p.MDN, p.LN].filter(Boolean).join(' ').trim()
  return name || p.MID || 'Unknown'
}

function patronKey(p) {
  return p.MID || p.MasterProfileID || patronLabel(p)
}

/** Build one or more GetProfile bodies for partial name / ID matching. */
function buildProfileSearchBodies(query) {
  const q = query.trim()
  if (q.length < MIN_SEARCH_CHARS) return []

  const base = { PageNumber: '1', PageSize: '12' }
  const parts = q.split(/\s+/).filter(Boolean)

  if (parts.length >= 2) {
    return [{ ...base, Firstname: parts[0], Lastname: parts.slice(1).join(' ') }]
  }

  const bodies = [
    { ...base, Firstname: q },
    { ...base, Lastname: q },
  ]
  if (/^[A-Za-z0-9_-]+$/.test(q)) {
    bodies.push({ ...base, MemberID: q })
  }
  return bodies
}

async function fetchPatronMatches(query) {
  const bodies = buildProfileSearchBodies(query)
  if (bodies.length === 0) return []

  const responses = await Promise.all(
    bodies.map(async (body) => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/GetProfile'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        const j = await r.json()
        if (j.ResponseCode !== '0' && j.ResponseCode !== 0) return []
        return Array.isArray(j.OutputData) ? j.OutputData : []
      } catch {
        return []
      }
    })
  )

  const seen = new Set()
  const merged = []
  for (const rows of responses) {
    for (const row of rows) {
      const key = patronKey(row)
      if (!key || seen.has(key)) continue
      seen.add(key)
      merged.push(row)
    }
  }
  return merged.slice(0, 12)
}

export default function NewEcddModal({ open, onClose, venues = [], onCreated }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [searchError, setSearchError] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)

  const [selectedPatron, setSelectedPatron] = useState(null)
  const [manualMemberId, setManualMemberId] = useState('')
  const [manualMemberName, setManualMemberName] = useState('')
  const [venueId, setVenueId] = useState('')
  const [triggerType, setTriggerType] = useState('manual')
  const [triggerDescription, setTriggerDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState('')
  const [warning, setWarning] = useState('')

  const debounceRef = useRef(null)
  const searchSeqRef = useRef(0)
  const patronFieldRef = useRef(null)
  const blurCloseRef = useRef(null)

  const resetForm = useCallback(() => {
    setSearchQuery('')
    setSearchResults([])
    setSearchError('')
    setDropdownOpen(false)
    setSelectedPatron(null)
    setManualMemberId('')
    setManualMemberName('')
    setVenueId(venues[0]?.id || '')
    setTriggerType('manual')
    setTriggerDescription('')
    setSubmitError('')
    setWarning('')
  }, [venues])

  useEffect(() => {
    if (open) resetForm()
  }, [open, resetForm])

  useEffect(() => {
    if (open && !venueId && venues.length > 0) {
      setVenueId(venues[0].id)
    }
  }, [open, venueId, venues])

  const runTypeaheadSearch = useCallback(async (query) => {
    const q = query.trim()
    if (q.length < MIN_SEARCH_CHARS) {
      setSearchResults([])
      setSearchError('')
      setSearching(false)
      return
    }

    const seq = ++searchSeqRef.current
    setSearching(true)
    setSearchError('')

    try {
      const rows = await fetchPatronMatches(q)
      if (seq !== searchSeqRef.current) return
      setSearchResults(rows)
      setSearchError(rows.length === 0 ? 'No patrons found — try another spelling or enter member ID manually.' : '')
      setDropdownOpen(true)
    } catch (e) {
      if (seq !== searchSeqRef.current) return
      setSearchResults([])
      setSearchError(e.message || 'Search unavailable — enter member ID manually.')
      setDropdownOpen(true)
    } finally {
      if (seq === searchSeqRef.current) setSearching(false)
    }
  }, [])

  useEffect(() => {
    if (!open || selectedPatron) return

    if (debounceRef.current) clearTimeout(debounceRef.current)
    const q = searchQuery.trim()

    if (q.length < MIN_SEARCH_CHARS) {
      setSearchResults([])
      setSearchError('')
      setSearching(false)
      return
    }

    setSearching(true)
    debounceRef.current = setTimeout(() => {
      runTypeaheadSearch(q)
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [searchQuery, open, selectedPatron, runTypeaheadSearch])

  const selectPatron = (p) => {
    if (blurCloseRef.current) {
      clearTimeout(blurCloseRef.current)
      blurCloseRef.current = null
    }
    setSelectedPatron(p)
    setSearchQuery(patronLabel(p))
    setSearchResults([])
    setDropdownOpen(false)
    setSearchError('')
    setManualMemberId('')
    setManualMemberName('')
  }

  const clearPatronSelection = () => {
    setSelectedPatron(null)
    setSearchQuery('')
    setSearchResults([])
    setDropdownOpen(false)
    patronFieldRef.current?.focus()
  }

  const handleSearchInputChange = (e) => {
    const next = e.target.value
    setSearchQuery(next)
    if (selectedPatron && next !== patronLabel(selectedPatron)) {
      setSelectedPatron(null)
    }
    if (next.trim().length >= MIN_SEARCH_CHARS) {
      setDropdownOpen(true)
    } else {
      setDropdownOpen(false)
      setSearchResults([])
      setSearchError('')
    }
  }

  const resolvedMemberId = selectedPatron?.MID || manualMemberId.trim()
  const resolvedMemberName = selectedPatron
    ? patronLabel(selectedPatron)
    : manualMemberName.trim()
  const resolvedMasterProfileId = selectedPatron?.MasterProfileID || null

  const showDropdown =
    dropdownOpen &&
    !selectedPatron &&
    searchQuery.trim().length >= MIN_SEARCH_CHARS

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (submitting) return
    setSubmitError('')
    setWarning('')

    if (!resolvedMemberId) {
      setSubmitError('Select a patron or enter a member ID.')
      return
    }
    if (!venueId) {
      setSubmitError('Select a venue.')
      return
    }
    if (!triggerDescription.trim()) {
      setSubmitError('Enter a reason for opening this ECDD.')
      return
    }

    setSubmitting(true)
    try {
      const r = await authFetch(getLocalApiUrl('/api/ecdd'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          member_id: resolvedMemberId,
          member_name: resolvedMemberName || resolvedMemberId,
          master_profile_id: resolvedMasterProfileId || undefined,
          venue_id: venueId,
          trigger_type: triggerType,
          trigger_description: triggerDescription.trim(),
        }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || !j.success) {
        throw new Error(j.error || `HTTP ${r.status}`)
      }
      if (j.data?.open_ecdd_warning) setWarning(j.data.open_ecdd_warning)
      const ecddId = j.data?.ecdd_id || j.data?.id
      if (!ecddId) throw new Error('Server did not return an ECDD id')
      onCreated?.({ ecddId, warning: j.data?.open_ecdd_warning, memberId: resolvedMemberId, memberName: resolvedMemberName })
    } catch (err) {
      setSubmitError(err.message || 'Failed to create ECDD')
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} role="presentation" aria-hidden />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[92vh] flex flex-col border border-gray-100"
        role="dialog"
        aria-labelledby="new-ecdd-title"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 id="new-ecdd-title" className="text-lg font-semibold text-psg-teal m-0">New ECDD</h2>
            <p className="text-xs text-gray-500 mt-1 m-0">
              Open enhanced due diligence without an investigation. Case-linked ECDDs are still created from investigations.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close">
            <XMarkIcon className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <form id="new-ecdd-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label htmlFor="ecdd-patron-search" className="block text-sm font-semibold text-gray-700 mb-1">
              Find patron
            </label>
            <p className="text-xs text-gray-500 mb-1.5">
              Type at least {MIN_SEARCH_CHARS} letters — names appear as you type.
            </p>

            {selectedPatron ? (
              <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                <CheckCircleIcon className="w-5 h-5 text-green-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-green-900 truncate">{patronLabel(selectedPatron)}</div>
                  <div className="text-xs font-mono text-green-700">{selectedPatron.MID}</div>
                </div>
                <button
                  type="button"
                  onClick={clearPatronSelection}
                  className="text-xs font-semibold text-green-800 hover:text-green-900 shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input
                    ref={patronFieldRef}
                    id="ecdd-patron-search"
                    type="text"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                    onFocus={() => {
                      if (searchQuery.trim().length >= MIN_SEARCH_CHARS) setDropdownOpen(true)
                    }}
                    onBlur={() => {
                      blurCloseRef.current = setTimeout(() => setDropdownOpen(false), 150)
                    }}
                    placeholder="Name or member ID…"
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-controls="ecdd-patron-listbox"
                    className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-psg-teal/20 focus:border-psg-teal/40"
                  />
                </div>

                {showDropdown && (
                  <ul
                    id="ecdd-patron-listbox"
                    role="listbox"
                    className="absolute z-10 mt-1 w-full max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg divide-y divide-gray-50"
                  >
                    {searching && searchResults.length === 0 && (
                      <li className="px-3 py-2.5 text-sm text-gray-500">Searching…</li>
                    )}
                    {!searching && searchResults.length === 0 && (
                      <li className="px-3 py-2.5 text-sm text-amber-700">
                        {searchError || 'No patrons found'}
                      </li>
                    )}
                    {searchResults.map((p) => (
                      <li key={patronKey(p)} role="option">
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectPatron(p)}
                          className="w-full text-left px-3 py-2.5 text-sm hover:bg-psg-teal/5 focus:bg-psg-teal/5 focus:outline-none"
                        >
                          <span className="font-medium text-psg-teal">{patronLabel(p)}</span>
                          <span className="text-gray-500 font-mono ml-2 text-xs">{p.MID}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {!selectedPatron && searchError && !showDropdown && (
              <p className="text-xs text-amber-700 mt-1">{searchError}</p>
            )}
          </div>

          {!selectedPatron && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="ecdd-manual-mid" className="block text-sm font-semibold text-gray-700 mb-1">
                  Or member ID
                </label>
                <input
                  id="ecdd-manual-mid"
                  value={manualMemberId}
                  onChange={(e) => setManualMemberId(e.target.value)}
                  placeholder="e.g. 8383773"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono"
                />
              </div>
              <div>
                <label htmlFor="ecdd-manual-name" className="block text-sm font-semibold text-gray-700 mb-1">
                  Display name
                </label>
                <input
                  id="ecdd-manual-name"
                  value={manualMemberName}
                  onChange={(e) => setManualMemberName(e.target.value)}
                  placeholder="Patron name"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="ecdd-venue" className="block text-sm font-semibold text-gray-700 mb-1">Venue</label>
              <select
                id="ecdd-venue"
                value={venueId}
                onChange={(e) => setVenueId(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                required
              >
                {venues.length === 0 ? (
                  <option value="">No venues loaded</option>
                ) : (
                  venues.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))
                )}
              </select>
            </div>
            <div>
              <label htmlFor="ecdd-trigger-type" className="block text-sm font-semibold text-gray-700 mb-1">Trigger type</label>
              <select
                id="ecdd-trigger-type"
                value={triggerType}
                onChange={(e) => setTriggerType(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
              >
                {TRIGGER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="ecdd-reason" className="block text-sm font-semibold text-gray-700 mb-1">Reason</label>
            <textarea
              id="ecdd-reason"
              value={triggerDescription}
              onChange={(e) => setTriggerDescription(e.target.value)}
              rows={3}
              placeholder="Why is ECDD being opened? e.g. Manager observed large cash bag, annual PEP review…"
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm resize-y min-h-[72px]"
              required
            />
          </div>

          {submitError && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{submitError}</div>
          )}
          {warning && (
            <div className="flex gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
              <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
              <span>{warning}</span>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" className="bg-psg-teal hover:bg-psg-teal-700" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create ECDD'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
